import { t } from '@lingui/macro';
import { I18n } from '@lingui/core';
import { MetricField, MetricUnit } from 'src/__generated__/graphql';
import { getMetricDisplayName } from '@/utils/i18n/getMetricDisplayName';

function getMetricFieldName(i18n: I18n, metricField: MetricField) {
  const metricFieldNameMap: Partial<Record<MetricField, string>> = {
    [MetricField.Flow]: t(i18n)`Caudal (Q)`,
    [MetricField.AccumulatedVolume]: t(i18n)`Volumen Acumulado`,
    [MetricField.Angle]: t(i18n)`Ángulo`,
    [MetricField.Conductivity]: t(i18n)`Conductividad`,
    [MetricField.ConnectionState]: t(i18n)`Estado de Conexión`,
    [MetricField.DgaAccumulatedVolume]: t(i18n)`Volumen Acumulado DGA`,
    [MetricField.DgaFlow]: t(i18n)`Caudal DGA`,
    [MetricField.DgaLevel]: t(i18n)`Nivel DGA`,
    [MetricField.DissolvedOxygen]: t(i18n)`Oxígeno Disuelto`,
    [MetricField.DissolvedOxygenSaturation]: t(i18n)`Saturación de Oxígeno Disuelto`,
    [MetricField.DistributionAlerts]: t(i18n)`Alertas de Distribución`,
    [MetricField.Energy]: t(i18n)`Energía`,
    [MetricField.FlowAlerts]: t(i18n)`Alertas de Caudal`,
    [MetricField.Level]: t(i18n)`Nivel (H)`,
    [MetricField.LevelRaw]: t(i18n)`Nivel bruto`,
    [MetricField.LevelAlerts]: t(i18n)`Alertas de Nivel`,
    [MetricField.OriginalDistance]: t(i18n)`Distancia del Sensor`,
    [MetricField.Orp]: t(i18n)`ORP`,
    [MetricField.Ph]: t(i18n)`pH`,
    [MetricField.Position]: t(i18n)`Posición`,
    [MetricField.RatingCurvesLimits]: t(i18n)`Límites de Curvas de Calibración`,
    [MetricField.SignalQuality]: t(i18n)`Calidad de señal`,
    [MetricField.SnowLevel]: t(i18n)`Nivel de Nieve`,
    [MetricField.SnowRadiation]: t(i18n)`Radiación`,
    [MetricField.SnowTemperature]: t(i18n)`Temperatura de Nieve`,
    [MetricField.SnowWaterEquivalent]: t(i18n)`Equivalente de agua de nieve`,
    [MetricField.Temperature]: t(i18n)`Temperatura`,
    [MetricField.TurbidityFnu]: t(i18n)`Turbidez FNU`,
    [MetricField.TurbidityNtu]: t(i18n)`Turbidez NTU`,
    [MetricField.Velocity]: t(i18n)`Velocidad`,
    [MetricField.SnowLevelNonCalibrated]: t(i18n)`Distancia sensor`,
    [MetricField.SnowWaterEquivalentNonCalibrated]: t(
      i18n,
    )`Equivalente de agua de nieve sin calibrar`,
    [MetricField.SnowWeight]: t(i18n)`Masa de Nieve`,
    [MetricField.VolumeHour]: t(i18n)`Volumen por hora`,
    [MetricField.VolumeDay]: t(i18n)`Volumen por día`,
    [MetricField.VolumeMonth]: t(i18n)`Volumen por mes`,
  };
  return metricFieldNameMap[metricField] ?? getMetricDisplayName(metricField, i18n);
}

type ReportsMetricUnit = { label: string; value: string; enum: MetricUnit };

const flowUnits: ReportsMetricUnit[] = [
  {
    label: 'L/s',
    value: 'liter per second',
    enum: MetricUnit.LiterPerSecond,
  },
  {
    label: 'm³/s',
    value: 'cubic meter per second',
    enum: MetricUnit.CubicMeterPerSecond,
  },
  {
    label: 'm³/h',
    value: 'cubic meter per hour',
    enum: MetricUnit.CubicMeterPerHour,
  },
];

const levelUnits: ReportsMetricUnit[] = [
  {
    label: 'cm',
    value: 'centimeter',
    enum: MetricUnit.Centimeter,
  },
  {
    label: 'm',
    value: 'meter',
    enum: MetricUnit.Meter,
  },
  {
    label: 'mm',
    value: 'millimeter',
    enum: MetricUnit.Millimeter,
  },
];

const velocityUnits: ReportsMetricUnit[] = [
  {
    label: 'mm/s',
    value: 'millimeter per second',
    enum: MetricUnit.MillimeterPerSecond,
  },
];

const volumeUnits: ReportsMetricUnit[] = [
  {
    label: 'm³',
    value: 'cubic meter',
    enum: MetricUnit.CubicMeter,
  },
  {
    label: 'L',
    value: 'liter',
    enum: MetricUnit.Liter,
  },
];

const temperatureUnits: ReportsMetricUnit[] = [
  {
    label: '°C',
    value: 'celsius',
    enum: MetricUnit.Celsius,
  },
];

const phUnits: ReportsMetricUnit[] = [
  {
    label: '',
    value: 'pH',
    enum: MetricUnit.Ph,
  },
];

const turbidityFnuUnits: ReportsMetricUnit[] = [
  {
    label: 'FNU',
    value: 'fnu',
    enum: MetricUnit.Fnu,
  },
];

const turbidityNtuUnits: ReportsMetricUnit[] = [
  {
    label: 'NTU',
    value: 'ntu',
    enum: MetricUnit.Ntu,
  },
];

const angleUnits: ReportsMetricUnit[] = [
  {
    label: '°',
    value: 'degree',
    enum: MetricUnit.Degree,
  },
];

const conductivityUnits: ReportsMetricUnit[] = [
  {
    label: 'mS/cm',
    value: 'millisiemens per centimeter',
    enum: MetricUnit.MillisiemensPerCentimeter,
  },
  {
    label: 'µS/cm',
    value: 'microsiemens per centimeter',
    enum: MetricUnit.MicrosiemensPerCentimeter,
  },
];

const densityUnits: ReportsMetricUnit[] = [
  {
    label: 'mg/L',
    value: 'milligram per liter',
    enum: MetricUnit.MilligramPerLiter,
  },
];

const percentUnits: ReportsMetricUnit[] = [
  {
    label: '%',
    value: 'percent',
    enum: MetricUnit.Percent,
  },
];
// Métricas adimensionales: el input del reporte exige una unidad, y `None` no se muestra.
const noneUnits: ReportsMetricUnit[] = [
  {
    label: '',
    value: 'none',
    enum: MetricUnit.None,
  },
];

const voltageUnits: ReportsMetricUnit[] = [
  {
    label: 'mV',
    value: 'millivolts',
    enum: MetricUnit.Millivolt,
  },
];

const intensityUnits: ReportsMetricUnit[] = [
  {
    label: 'w/m²',
    value: 'watt per square meter',
    enum: MetricUnit.WattPerSquareMeter,
  },
];

const weightUnits: ReportsMetricUnit[] = [
  {
    label: 'kg',
    value: 'kilogram',
    enum: MetricUnit.Kilogram,
  },
];

const metricUnitsMapper: Partial<Record<MetricField, ReportsMetricUnit[]>> = {
  [MetricField.Flow]: flowUnits,
  [MetricField.AccumulatedVolume]: volumeUnits,
  [MetricField.Angle]: angleUnits,
  [MetricField.Conductivity]: conductivityUnits,
  [MetricField.ConnectionState]: [],
  [MetricField.DgaAccumulatedVolume]: volumeUnits,
  [MetricField.DgaFlow]: [],
  [MetricField.DgaLevel]: [],
  [MetricField.DissolvedOxygen]: densityUnits,
  [MetricField.DissolvedOxygenSaturation]: percentUnits,
  [MetricField.DistributionAlerts]: [],
  [MetricField.Energy]: [],
  [MetricField.FlowAlerts]: [],
  [MetricField.Level]: levelUnits,
  [MetricField.LevelRaw]: levelUnits,
  [MetricField.LevelAlerts]: [],
  [MetricField.OriginalDistance]: levelUnits,
  [MetricField.Orp]: voltageUnits,
  [MetricField.Ph]: phUnits,
  [MetricField.Position]: levelUnits,
  [MetricField.RatingCurvesLimits]: [],
  [MetricField.SignalQuality]: percentUnits,
  [MetricField.SignalStrength]: noneUnits,
  [MetricField.SignalNoiseRatio]: noneUnits,
  [MetricField.SnowLevel]: levelUnits,
  [MetricField.SnowRadiation]: intensityUnits,
  [MetricField.SnowTemperature]: temperatureUnits,
  [MetricField.SnowWaterEquivalent]: levelUnits,
  [MetricField.Temperature]: temperatureUnits,
  [MetricField.TurbidityFnu]: turbidityFnuUnits,
  [MetricField.TurbidityNtu]: turbidityNtuUnits,
  [MetricField.Velocity]: velocityUnits,
  [MetricField.SnowLevelNonCalibrated]: levelUnits,
  [MetricField.SnowWaterEquivalentNonCalibrated]: levelUnits,
  [MetricField.SnowWeight]: weightUnits,
  [MetricField.VolumeHour]: volumeUnits,
  [MetricField.VolumeDay]: volumeUnits,
  [MetricField.VolumeMonth]: volumeUnits,
};

function getMetricUnits(metricField: MetricField) {
  return metricUnitsMapper[metricField] ?? [];
}

export { getMetricFieldName, getMetricUnits };
